import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// Define interfaces for Region and the API response
export interface Region { // Exported for use in other files like Regions.tsx
  id: string;
  name: string;
  divisionId?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface RegionsListResponse {
  regions: Region[];
}

const backendApi = config.backendApi;

// The centralized fetch function - removed _organizationId as it's not directly used in the API path
const fetchRegions = async (): Promise<Region[]> => {
  // Assuming the backend API for regions implicitly handles organizationId based on authentication,
  // or that it's a global resource for the authenticated organization.
  // If your API requires organizationId in the URL or as a query param, adjust this line:
  // e.g., `${backendApi}/api/organizations/${organizationId}/regions`
  const response = await axios.get<RegionsListResponse>(`${backendApi}/api/regions`, { withCredentials: true });
  // Defensive: tolerate an unexpected response shape instead of handing a
  // non-array to callers that `.map`/`.filter` it.
  return Array.isArray(response.data.regions) ? response.data.regions : [];
};

// The custom hook
export function useRegions(organizationId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Region[]>({
    queryKey: ['regions', organizationId], // Include organizationId in queryKey
    queryFn: () => {
      if (!organizationId) {
        // This case should ideally be prevented by 'enabled' but provides type safety
        throw new Error("organizationId is required to fetch regions.");
      }
      return fetchRegions(); // Call fetcher without organizationId parameter
    },
    // Enable the query only if organizationId is available, as it's a common dependency
    enabled: !!organizationId && contextReady,
  });
}

export function useRegion(regionId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Region>({
    queryKey: ['region', regionId],
    queryFn: async () => {
      if (!regionId) throw new Error('regionId required');
      const regions = await fetchRegions();
      const region = regions.find((r) => r.id === regionId);
      if (!region) throw new Error('Region not found');
      return region;
    },
    enabled: !!regionId && contextReady,
  });
}
